import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BtnGhost, BtnPrimary, Field, Select } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDateTime, STAFF_ROLES } from '@/lib/labels'

type Staff = {
  id: string
  email: string
  fullName: string
  role: string
  active: boolean
  lastSeenAt: string | null
  totpEnrolled: boolean
}

export function Utilisateurs() {
  const toast = useToast()
  const qc = useQueryClient()
  const [edit, setEdit] = useState<Staff | null>(null)
  const [role, setRole] = useState('SALES')
  const [active, setActive] = useState(true)

  const list = useQuery({
    queryKey: ['admin-staff'],
    queryFn: () => api<{ items: Staff[] }>('/admin/staff'),
  })

  const patch = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/staff/${edit!.id}`, 'PATCH', { role, active }),
    onSuccess: () => {
      toast.ok('Utilisateur mis à jour')
      setEdit(null)
      void qc.invalidateQueries({ queryKey: ['admin-staff'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Aucun compte interne" />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Nom', 'E-mail', 'Rôle', '2FA', 'Actif', 'Vu', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5 font-medium">{s.fullName}</td>
                  <td className="px-4 py-2.5">{s.email}</td>
                  <td className="px-4 py-2.5">{s.role}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill
                      label={s.totpEnrolled ? 'Oui' : 'Non'}
                      tone={s.totpEnrolled ? 'ok' : 'warn'}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusPill
                      label={s.active ? 'Actif' : 'Inactif'}
                      tone={s.active ? 'ok' : 'bad'}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(s.lastSeenAt)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <RowActions
                      items={[
                        {
                          label: 'Modifier',
                          onSelect: () => {
                            setEdit(s)
                            setRole(s.role)
                            setActive(s.active)
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={Boolean(edit)}
        title={edit ? `Modifier ${edit.fullName}` : 'Utilisateur'}
        onClose={() => setEdit(null)}
        footer={
          <>
            <BtnGhost onClick={() => setEdit(null)}>Annuler</BtnGhost>
            <BtnPrimary disabled={patch.isPending} onClick={() => patch.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Rôle">
            <Select value={role} onChange={(e) => setRole(e.target.value)}>
              {STAFF_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Compte actif
          </label>
        </div>
      </Modal>
    </div>
  )
}
